import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';
import CompanyService from '@/services/company-service';
import employeeService from '@/services/employee/employee-service';
import IntegrationService from '@/services/integration-service';
import importService from '@/services/import-service';
import accountManagerService from '@/services/account-manager/account-manager-service';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import { accountManagerFromResponse, ActiveIntegration, computeSteps, countTeamMembers, DemoFromMeetings, StepsResult } from '@/PageComponents/Onboarding/onboarding-rules';
import { DemoBooking } from '@/PageComponents/Onboarding/onboarding-stub-store';
import { useDemoBooking } from '@/PageComponents/Onboarding/useDemoBooking';
import { OnboardingManager } from '@/PageComponents/Onboarding/onboarding-config';
import { loadOnboardingSkips, OnboardingSkip, OnboardingSkips, saveOnboardingSkip } from '@/PageComponents/Onboarding/onboarding-user-config';

export interface OnboardingStatus {
    isLoading: boolean;
    hasError: boolean;
    retry: () => void;
    company: any | null;
    integration: ActiveIntegration | null;
    completedImportCount: number | null;
    /** All jobs, closed and archived included; null when they couldn't load. */
    jobCount: number | null;
    steps: StepsResult;
    /** The demo as HubSpot reports it (GET /Meeting/List). */
    demo: DemoFromMeetings;
    /**
     * The booking to show: HubSpot's when it has one, otherwise a booking just made in the iframe
     * while HubSpot's copy is still on its way (see resolveDemoBooking).
     */
    demoBooking: DemoBooking | undefined;
    /** The tenant's account manager (GET /AccountManager/Get); null when none is assigned or it couldn't load. */
    accountManager: OnboardingManager | null;
    /** The logo's "No logo yet" (step 2), from the user's dashboard config. */
    skips: OnboardingSkips;
    skip: (which: OnboardingSkip) => void;
    /** Records a booking made in the iframe; shared app-wide (see useDemoBooking). */
    setLocalDemoBooking: (booking: DemoBooking | undefined) => void;
    refetchCompany: () => void;
    refetchJobs: () => void;
    refetchEmployees: () => void;
}

const asCount = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

// utils/Fetch resolves (rather than throws) on 4xx/5xx, so reject unusable responses here
// to turn them into query errors.
const ensure = <T,>(response: T, valid: (r: any) => boolean): T => {
    if (!response || (response as any).status >= 400 || !valid(response)) throw new Error('onboarding request failed');
    return response;
};
const isCount = (v: unknown) => typeof v === 'number' && Number.isFinite(v);

const SKIPS_KEY = ['onboarding', 'skips'];
const NO_SKIPS: OnboardingSkips = { logo: false };

export const useOnboardingStatus = (enabled: boolean): OnboardingStatus => {
    const options = { enabled, retry: false } as const;

    const companyQuery = useQuery({ queryKey: ['onboarding', 'company'], queryFn: async () => ensure(await CompanyService.getCompany(), r => !!r.Name) /* GET /Company has no ID field */, ...options });
    const employeesQuery = useQuery({ queryKey: ['onboarding', 'employees'], queryFn: async () => ensure(await employeeService.getEmployees(null, false), r => isCount(r.TotalResults) || Array.isArray(r.Results)), ...options });
    const integrationQuery = useQuery({ queryKey: ['onboarding', 'integration'], queryFn: () => IntegrationService.getIntegration(), ...options });
    const importsQuery = useQuery({
        queryKey: ['onboarding', 'imports'],
        queryFn: async () => ensure(await importService.getImports(1, 0, null, null, '', [], [String(Enums.ImportStatus.Completed)]) /* the API 500s on numeric statuses; ImportsTable sends strings */, r => isCount(r.total)),
        ...options,
    });
    const jobsQuery = useQuery({
        queryKey: ['onboarding', 'jobs'],
        queryFn: async () => ensure(await Fetch.post({ url: '/Job/GetJobs', params: { pageSize: 1, pageIndex: 0, searchPhrase: '', IncludeClosed: true, ShowArchived: true } }), r => isCount(r.TotalResults)),
        ...options,
    });

    // Not part of `queries`: the dashboard works without meetings, so a failure here shouldn't raise
    // the retry alert. It still holds the skeleton so the first render (and the Before-you-start
    // modal) already knows about a demo booked before sign-up.
    const demoBooking = useDemoBooking(enabled);

    // Also outside `queries`: without an answer the card keeps showing the demo manager, which is
    // what the API itself answers when Manager is down. It holds the skeleton so the card doesn't
    // switch from the demo manager to the account manager after the first render.
    const accountManagerQuery = useQuery({
        queryKey: ['onboarding', 'accountManager'],
        queryFn: async () => accountManagerFromResponse(await accountManagerService.getAccountManager()),
        ...options,
    });

    // Also outside `queries`: if it fails, nothing shows as skipped.
    const skipsQuery = useQuery({ queryKey: SKIPS_KEY, queryFn: loadOnboardingSkips, ...options });
    const queryClient = useQueryClient();
    const skips = skipsQuery.data ?? NO_SKIPS;
    const skip = useCallback((which: OnboardingSkip) => {
        const set = (value: boolean) => queryClient.setQueryData<OnboardingSkips>(SKIPS_KEY, prev => ({ ...(prev ?? NO_SKIPS), [which]: value }));
        set(true);
        saveOnboardingSkip(which, true).catch(() => {
            set(false);
            showNotification({ color: 'yellow.7', message: "We couldn't save that. Please try again." });
        });
    }, [queryClient]);

    const queries = [companyQuery, employeesQuery, integrationQuery, importsQuery, jobsQuery];
    // v4 reports isLoading for disabled queries too — only count ones actually fetching.
    const anyLoading = demoBooking.isLoading || [...queries, accountManagerQuery, skipsQuery].some(q => q.isLoading && q.fetchStatus !== 'idle');
    // Only the first load shows the skeleton: a Retry flips errored queries back to loading,
    // which would otherwise unmount the dashboard and lose its local state.
    const hasSettled = useRef(false);
    if (enabled && !anyLoading) hasSettled.current = true;
    const isLoading = anyLoading && !hasSettled.current;
    const hasError = queries.some(q => q.isError);

    const company = companyQuery.data ?? null;
    const employees: any = employeesQuery.data;
    const integration: ActiveIntegration | null = integrationQuery.data ?? null;
    const completedImportCount = asCount((importsQuery.data as any)?.total);
    const jobCount = asCount((jobsQuery.data as any)?.TotalResults);

    const steps = useMemo(() => computeSteps({
        // Counted from the rows, which carry each invite's status; the total includes revoked invites.
        employeeCount: Array.isArray(employees?.Results) ? countTeamMembers(employees.Results) : asCount(employees?.TotalResults),
        logoSkipped: skips.logo,
        hasLogo: company ? !!company.LogoUrl : null,
        integration,
        completedImportCount,
        jobCount,
    }), [employees, skips, company, integration, completedImportCount, jobCount]);

    return {
        isLoading,
        hasError,
        retry: () => queries.forEach(q => { if (q.isError) q.refetch(); }),
        company,
        integration,
        completedImportCount,
        jobCount,
        steps,
        demo: demoBooking.demo,
        demoBooking: demoBooking.booking,
        accountManager: accountManagerQuery.data ?? null,
        skips,
        skip,
        setLocalDemoBooking: demoBooking.setLocalBooking,
        refetchCompany: () => { companyQuery.refetch(); },
        refetchJobs: () => { jobsQuery.refetch(); },
        refetchEmployees: () => { employeesQuery.refetch(); },
    };
};
